import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDrinkCustomization } from "../contexts/DrinkCustomizationContext";
import useInactivityTimer from "../hooks/useInactivityTimer";
import showInactivityPrompt from "../utils/showInactivityPrompt";
import * as apiService from '../services/apiService';
import { useAppSettings } from '../contexts/AppSettingsContext';
import BackButton from '../components/BackButton';
import WaterCard from '../components/drink_selection/WaterCard';
import FlavoredDrinkCard from '../components/drink_selection/FlavoredDrinkCard';
import { Drink } from '../types/DrinkTypes';

const DrinkSelectionScreen: React.FC = () => {
  const { settings, appSettingsReady } = useAppSettings();
  const navigate = useNavigate();
  const { drinks, isLoading } = useDrinkCustomization();
  const [selectedDrinkId, setSelectedDrinkId] = useState<number | null>(null);

  const handleInactivity = () => {
    navigate("/");
  };

  useInactivityTimer(
    settings.inactivityTimeout,
    () => {
      showInactivityPrompt(handleInactivity);
    }
  );


  useEffect(() => {
    // Turn off the bp LED when entering the screen
    apiService.turnOffBpLed().catch(error => console.error('Failed to turn off bp LED:', error));
  }, []);

  const handleDrinkSelect = async (drink: Drink) => {
    setSelectedDrinkId(drink.id);
    
    try {
        const selectionData = {
            drink_id: drink.id,
            fountain_machine: drinks.fountain_machine || 1,
        };
        const transaction = await apiService.postDrinkSelection(selectionData);
        setTimeout(() => {
            if (transaction.payment_required) {
                navigate("/payment", { state: { transaction_id: transaction.transaction_id } });
            } else {
                navigate("/dispensing", { state: { transaction_id: transaction.transaction_id } });
            }
        }, 300);
    } catch (error) {
        console.error("Failed to create transaction:", error);
        setSelectedDrinkId(null);
    }
  };

  if (!appSettingsReady || isLoading) {
    return (
      <div className="w-[1080px] h-[1920px] flex justify-center items-center bg-gray-100">
        <div className="text-2xl text-gray-500">Loading...</div>
      </div>
    );
  }

  const selectedStyle = "shadow-[0px_-1px_4px_2px_rgba(2,185,184,1.00)] outline outline-[13px] outline-teal-500";

  return (
    <div data-layer="PAGE 3" className="Page3 w-[1080px] h-[1920px] relative bg-white overflow-hidden">
      <BackButton onClick={() => navigate("/")} />
      <img data-layer="image-fond-kissd-3 1" className="ImageFondKissd31 w-[1281px] h-[1927px] absolute" src="/assets/images/new_design/drink_selection/fond/image-fond-kissd-3-1.png" />
      <div data-layer="Rectangle 2" className="Rectangle2 w-[1080px] h-[1920px] left-0 top-0 absolute bg-teal-500/40" />

      <div data-layer="écran" className="Cran w-[906px] h-[1673px] left-[86px] top-[172px] absolute bg-white/30 rounded-[62px] shadow-[6px_7px_27.700000762939453px_-11px_rgba(0,0,0,0.42)]" />
      <img data-layer="freepik__3d-floating-transparent-bubbles-with-green-and-blu__24199 1" className="Freepik3dFloatingTransparentBubblesWithGreenAndBlu241991 w-[150px] h-[447px] left-[10px] top-[-90px] absolute mix-blend-screen" src="/assets/images/new_design/drink_selection/freepik__3d-floating-transparent-bubbles-with-green-and-blu__24199-1.svg" />
      <img data-layer="freepik__3d-floating-transparent-bubbles-with-green-and-blu__24199 1" className="Freepik3dFloatingTransparentBubblesWithGreenAndBlu241991 w-56 h-80 left-[894px] top-[1580px] absolute mix-blend-screen" src="/assets/images/new_design/drink_selection/freepik__3d-floating-transparent-bubbles-with-green-and-blu__24199-1p.svg" />

      <div data-layer="Rectangle 1" className="Rectangle1 w-[732px] h-52 left-[173px] top-[75px] absolute bg-gray-900 rounded-[30px] shadow-[10px_10px_10px_2px_rgba(0,0,0,0.13)]" />
      <div data-layer="place bottle" className="PlaceBottle w-[694px] h-12 left-[192px] top-[80px] absolute text-center text-white text-5xl font-black font-['Montserrat'] uppercase leading-[115px]">place bottle</div>
      <div data-layer="and select your chilled ultra-filtered water" className="AndSelectYourChilledUltraFilteredWater w-[549px] h-20 left-[265px] top-[178px] absolute text-center text-teal-500 text-3xl font-black font-['Montserrat'] uppercase leading-9">and select your chilled ultra-filtered water</div>

      <div className="absolute top-[344px] left-[176px] grid grid-cols-2 gap-x-[81px] gap-y-[45px]">
        {drinks.drinks.map((drink) => {
            const isWater = drink.relay_number < 0;
            const price = parseFloat(drink.price) > 0 ? `${parseFloat(drink.price) * 100}p` : 'Free';

            if (isWater) {
                return (
                    <WaterCard
                        key={drink.id}
                        type={drink.type}
                        onClick={() => handleDrinkSelect(drink)}
                        isSelected={selectedDrinkId === drink.id}
                        selectedStyle={selectedStyle}
                    />
                );
            } else {
                const bubbles = drink.type === 'sparkling' ? [
                    { src: "/assets/images/new_design/drink_selection/bulles-fontaine-7.svg", className: "w-[150px] h-[100px] left-[20px] top-[73px] absolute z-20 origin-top-left opacity-36" },
                    { src: "/assets/images/new_design/drink_selection/bulles-fontaine-6.svg", className: "w-[150px] h-[100px] left-[152px] top-[10px] absolute z-20 origin-top-left opacity-36" }
                ] : undefined;

                return (
                    <FlavoredDrinkCard
                        key={drink.id}
                        drinkName={drink.drink_name}
                        type={drink.type}
                        tagline={drink.tagline}
                        price={price}
                        productImageSrc={`${settings.apiBaseUrl}${drink.product_image}`}
                        productImageClassName={drink.product_image_class_name}
                        flavorIconSrc={`${settings.apiBaseUrl}${drink.flavor_icon}`}
                        flavorIconTop={drink.flavor_icon_top}
                        flavorIconImageTop={drink.flavor_icon_image_top}
                        onClick={() => handleDrinkSelect(drink)}
                        isSelected={selectedDrinkId === drink.id}
                        selectedStyle={selectedStyle}
                        bubbles={bubbles}
                    />
                );
            }
        })}
      </div>

    </div>
  );
};

export default DrinkSelectionScreen;
